import { CircleUser, Network, ScrollText, Store } from 'lucide-react';
import {
  Accordion,
  Button,
  Checkbox,
  Divider,
  Flex,
  Group,
  LoadingOverlay,
  SegmentedControl,
  Select,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { discipline } from '../data/discipline.tsx';
import { professions } from '../data/professions.tsx';
import { regioni } from '../data/regioni.tsx';
import { useAuthActions, useAuthUser } from '../providers/AuthProvider.tsx';

export default function Profile() {
  const { t, i18n } = useTranslation();
  const user = useAuthUser();
  const { login } = useAuthActions();
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [opened, setOpened] = useState<string | null>(null);

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      type: 'student',
      first_name: '',
      last_name: '',
      full_name: '',
      fiscal_code: '',
      vat_number: '',
      sdi_number: '',
      address: '',
      role: '',
      contract: '',
      title: '',
      profession: '', // <-- string
      disciple: '', // <-- string
      order_of_psychologists: '',
      order_number: '',
      profile_photo_path: '',
      region: '',
      is_public: true,
      website: '',
    },
  });

  // Imposta i valori del form dall'utente loggato
  useEffect(() => {
    if (user) {
      form.setValues({
        ...user,
        profession: user.profession?.toString() || '',
        disciple: user.disciple?.toString() || '',
      });
    }
  }, [user]);

  // Genera le discipline in base alla professione scelta
  const discipleData = useMemo(() => {
    const profession = form.getValues().profession;
    if (profession && discipline[profession]) {
      return discipline[profession]
        .sort((s1: any, s2: any) =>
          s1.label.toLowerCase().localeCompare(s2.label.toLowerCase())
        )
        .map((s: any) => ({
          label: s.label,
          value: s.value.toString(),
        }));
    }
    return [];
  }, [form.getValues().profession]);

  const submit = (values: any) => {
    setInProgress(true);
    const filteredValues = Object.fromEntries(
      Object.entries(values)
        .filter(([, value]) => value !== '' && value !== null)
        .flatMap(([key, value]) => {
          if (typeof value === 'string') {
            value = value.trim();
            if (value === '') return [];
          }
          if (key === 'is_public') {
            return [[key, value ? 1 : 0]];
          }
          if (
            typeof value === 'string' &&
            (key === 'profession' || key === 'disciple')
          ) {
            return [[key, parseInt(value)]];
          }
          return [[key, value]];
        })
    );

    fetch(`${import.meta.env.VITE_API_URL}/services/me`, {
      method: 'PATCH',
      body: JSON.stringify(filteredValues),
      credentials: 'include',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(`HTTP ${response.status}: ${text}`);
          });
        }
        return response.json();
      })
      .then(data => {
        login(data.data);
        form.setValues({
          ...data.data,
          profession: data.data.profession?.toString() || '',
          disciple: data.data.disciple?.toString() || '',
        });
        notifications.show({
          color: 'teal',
          withBorder: true,
          radius: 'lg',
          autoClose: false,
          message: 'Profilo aggiornato',
        });
      })
      .catch(error => {
        notifications.show({
          color: 'red',
          withBorder: true,
          radius: 'lg',
          title: 'Errore',
          autoClose: false,
          message: error.message,
        });
      })
      .finally(() => {
        setInProgress(false);
      });
  };

  return (
    <>
      <Flex direction={'column'}>
        <Title>
          {user?.type === 'enterprise'
            ? t('user_profile_enterprise', 'PROFILO ORGANIZZAZIONE')
            : t('user_profile', 'PROFILO UTENTE')}
        </Title>
        <Title order={4} c={'dimmed'}>
          Le tue informazioni professionali
        </Title>
      </Flex>
      <Divider mb={'md'} mt={'md'} />
      <form
        onSubmit={form.onSubmit(values => submit(values))}
        style={{ position: 'relative' }}
      >
        <LoadingOverlay visible={inProgress} />
        <Accordion
          style={{ boxShadow: '0 4px 5px rgba(0,0,0,0.15)' }}
          defaultValue='-'
          variant='contained'
          radius='sm'
          mt={'lg'}
          onChange={setOpened}
        >
          <Accordion.Item value={'personal'}>
            <Accordion.Control icon={<CircleUser size={16} />}>
              {user?.type === 'enterprise'
                ? t('user_profile_enterprise', 'Informazioni Aziendali')
                : t('personal_info', 'Informazioni Personali')}
            </Accordion.Control>
            <Accordion.Panel>
              <Group mb={'sm'}>
                <Text size='sm' fw={500}>
                  {t('account_type', 'Tipologia di account:').replace('.', ':')}{' '}
                  {t(form.getValues().type)}
                </Text>
                <Text size='sm' c={'dimmed'}>
                  {t(
                    'more_info_c',
                    'Se desideri cambiare tipologia di account, contattaci qui: support@become-hub.com'
                  )}
                </Text>
              </Group>
              <SegmentedControl
                key={opened ? 'visible' : 'hidden'}
                mb={'md'}
                {...form.getInputProps('type')}
                readOnly
                fullWidth
                size='md'
                color='blue'
                data={[
                  {
                    label: t('enthusiast', 'Entusiasta'),
                    value: 'enthusiast',
                  },
                  {
                    label: t('student', 'Studente'),
                    value: 'student',
                  },
                  {
                    label: t('professional', 'Professionista'),
                    value: 'professional',
                  },
                  {
                    label: t('enterprise', 'Organizzazione'),
                    value: 'enterprise',
                  },
                ]}
              />

              <Group wrap={'wrap'} grow mt={'md'} mb={'md'}>
                <TextInput
                  {...form.getInputProps('first_name')}
                  label={t('first_name', 'Nome')}
                />
                <TextInput
                  {...form.getInputProps('last_name')}
                  label={t('last_name', 'Cognome')}
                />
              </Group>

              <Group wrap={'wrap'} grow mt={'md'} mb={'md'}>
                <TextInput
                  {...form.getInputProps('fiscal_code')}
                  label={t('fiscal_code', 'Codice Fiscale')}
                />
                {i18n.language === 'it' && (
                  <>
                    <Select
                      data={regioni}
                      {...form.getInputProps('order_of_psychologists')}
                      label={'Ordine degli psicologi'}
                    />
                    <TextInput
                      {...form.getInputProps('order_number')}
                      label={'Numero di appartenenza'}
                    />
                  </>
                )}
              </Group>
            </Accordion.Panel>
          </Accordion.Item>

          {i18n.language === 'it' && (
            <>
              <Accordion.Item value={'ecm'}>
                <Accordion.Control icon={<ScrollText size={16} />}>
                  Crediti ECM
                </Accordion.Control>
                <Accordion.Panel>
                  <Group wrap={'wrap'} grow mt={'md'} mb={'md'}>
                    <Select
                      data={[
                        {
                          label: 'Partecipante',
                          value: 'participant',
                        },
                        { label: 'Docente', value: 'teacher' },
                        { label: 'Tutor', value: 'tutor' },
                        { label: 'Relatore', value: 'speaker' },
                      ]}
                      {...form.getInputProps('role')}
                      label={'Ruolo'}
                    />

                    <Select
                      data={[
                        {
                          label: 'Libero professionista',
                          value: 'freelancer',
                        },
                        {
                          label: 'Dipendente convenzionato',
                          value: 'contracted_employee',
                        },
                        {
                          label: 'Nessuna occupazione',
                          value: 'no_occupation',
                        },
                      ]}
                      {...form.getInputProps('contract')}
                      label={'Tipo contratto'}
                    />

                    <TextInput
                      {...form.getInputProps('title')}
                      placeholder={'Dott.'}
                      label={'Titolo'}
                    />
                  </Group>
                  <Group wrap={'wrap'} grow mt={'md'} mb={'md'}>
                    <Select
                      searchable
                      data={professions
                        .sort((s1, s2) =>
                          s1.label
                            .toLowerCase()
                            .localeCompare(s2.label.toLowerCase())
                        )
                        .map(s => {
                          return {
                            label: s.label,
                            value: s.value.toString(),
                          };
                        })}
                      {...form.getInputProps('profession')}
                      label={'Professione'}
                    />

                    <Select
                      searchable
                      data={discipleData}
                      {...form.getInputProps('disciple')}
                      label={'Disciplina'}
                    />
                  </Group>
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value={'network'}>
                <Accordion.Control
                  icon={<Network size={16} />}
                >
                  Pagina Professionisti
                </Accordion.Control>
                <Accordion.Panel>
                  <Group wrap={'wrap'} grow mt={'md'} mb={'md'}>
                    <Checkbox
                      {...form.getInputProps('is_public', {
                        type: 'checkbox',
                      })}
                      readOnly
                      label={'Abilita'}
                    />
                    <TextInput
                      {...form.getInputProps('website')}
                      label={'Sito Web'}
                    />
                    <TextInput
                      {...form.getInputProps('region')}
                      label={'Regione'}
                    />
                    <TextInput
                      {...form.getInputProps('profile_photo_path')}
                      label={'Url foto profilo'}
                    />
                  </Group>
                </Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item value={'billing'}>
                <Accordion.Control icon={<Store size={16} />}>
                  Dati di Fatturazione
                </Accordion.Control>
                <Accordion.Panel>
                  <Group wrap={'wrap'} grow mt={'md'} mb={'md'}>
                    <TextInput
                      {...form.getInputProps('address')}
                      label={'Indirizzo di fatturazione'}
                    />
                    <TextInput
                      {...form.getInputProps('vat_number')}
                      label={'P.IVA'}
                    />
                    <TextInput
                      {...form.getInputProps('sdi_number')}
                      label={'SDI'}
                    />
                  </Group>
                </Accordion.Panel>
              </Accordion.Item>
            </>
          )}
        </Accordion>

        <Flex justify={'flex-end'} mt={'md'}>
          <Button type={'submit'} disabled={inProgress} loading={inProgress}>
            Salva
          </Button>
        </Flex>
      </form>
    </>
  );
}
